import {
  SankeyData,
  type SankeyLinkData,
  type SankeyNodeData,
  type SankeyLinkTooltipMeta,
} from '../../types/visualization';
import type { ApplicationState } from '../../redux/features/jobs/jobsSlice';

function pct(part: number, whole: number): number | undefined {
  if (whole <= 0) return undefined;
  return Math.round((part / whole) * 100);
}

interface Labels {
  browsed: string;
  viewed: string;
  saved: string;
  applied: string;
  interviewing?: string;
  offer?: string;
  rejected?: string;
  hired?: string;
}

interface JobLike { 
  id: string;
  applicationState?: ApplicationState;
}

/**
 * Builds a Sankey diagram showing the job application flow:
 * Browsed -> (Viewed/Saved) -> Applied -> (Further States)
 * 
 * Rules:
 * - Everything starts with "Browsed" (largest section)
 * - Splits into "Viewed" (viewed but NOT saved) and "Saved" (saved, including those also viewed)
 * - If a job is both saved and viewed, it appears only in "Saved"
 * - "Viewed" and "Saved" recombine into "Applied"
 * - "Applied" filters down to further states (interviewing, offer, rejected, hired)
 * - All further states must also be "applied"
 */
export function buildJobFlowSankey(
  browsedJobs: JobLike[],
  viewedJobs: JobLike[],
  savedJobs: JobLike[],
  appliedJobs: JobLike[],
  labels: Labels
): SankeyData {
  const browsedIds = new Set(browsedJobs.map((j) => j.id));
  const viewedIds = new Set(viewedJobs.map((j) => j.id));
  const savedIds = new Set(savedJobs.map((j) => j.id));
  const appliedIds = new Set(appliedJobs.map((j) => j.id));

  // Create a map of applied jobs with their application states
  const appliedJobMap = new Map<string, ApplicationState>();
  appliedJobs.forEach((job) => {
    appliedJobMap.set(job.id, job.applicationState ?? null);
  });

  // Calculate viewed-only (viewed but NOT saved)
  const viewedOnlyIds = new Set<string>();
  viewedIds.forEach((id) => {
    if (!savedIds.has(id)) {
      viewedOnlyIds.add(id);
    }
  });

  // Saved includes all saved jobs (including those that were also viewed)
  const savedCount = savedIds.size;
  const viewedOnlyCount = viewedOnlyIds.size;

  // Calculate applied counts
  // Applied can come from Viewed, Saved, or directly from Browsed
  let appliedFromViewedOnly = 0;
  let appliedFromSaved = 0;
  let appliedFromBrowsed = 0; // Applied but not in viewed or saved
  
  viewedOnlyIds.forEach((id) => {
    if (appliedIds.has(id)) {
      appliedFromViewedOnly++;
    }
  });
  
  savedIds.forEach((id) => {
    if (appliedIds.has(id)) {
      appliedFromSaved++;
    }
  });

  // Count applied jobs that are not in viewed or saved (applied directly from browsed)
  appliedIds.forEach((id) => {
    if (!viewedIds.has(id) && !savedIds.has(id) && browsedIds.has(id)) {
      appliedFromBrowsed++;
    }
  });

  const countedInFunnel =
    appliedFromViewedOnly + appliedFromSaved + appliedFromBrowsed;
  /** Applied jobs not present in browsed/viewed/saved (e.g. pasted external applications) */
  const appliedFromOutsideTrackedFunnel = Math.max(0, appliedIds.size - countedInFunnel);
  const totalApplied = appliedIds.size;

  // Calculate further states (all must be applied)
  let interviewingCount = 0;
  let offerCount = 0;
  let rejectedCount = 0;
  let hiredCount = 0;

  appliedJobMap.forEach((state) => {
    if (state === 'interviewing') interviewingCount++;
    else if (state === 'offer') offerCount++;
    else if (state === 'rejected') rejectedCount++;
    else if (state === 'hired') hiredCount++;
  });

  // Build nodes array with flow values for proportional sizing
  const nodes: SankeyNodeData[] = [];
  const nodeIndices: Record<string, number> = {};

  // Always start with Browsed - use total browsed count for sizing
  const browsedCount = browsedIds.size;
  nodes.push({
    name: labels.browsed,
    flow: browsedCount,
    jobFlowStage: 'browsed',
    tooltipMeta: { stage: 'browsed', jobCount: browsedCount },
  });
  nodeIndices.browsed = 0;

  let currentIndex = 1;

  // Add Viewed and Saved nodes if they have data
  if (viewedOnlyCount > 0) {
    nodes.push({
      name: labels.viewed,
      flow: viewedOnlyCount,
      jobFlowStage: 'viewed',
      tooltipMeta: {
        stage: 'viewed',
        jobCount: viewedOnlyCount,
        shareOfBrowsedPct: pct(viewedOnlyCount, browsedCount),
      },
    });
    nodeIndices.viewed = currentIndex++;
  }
  if (savedCount > 0) {
    nodes.push({
      name: labels.saved,
      flow: savedCount,
      jobFlowStage: 'saved',
      tooltipMeta: {
        stage: 'saved',
        jobCount: savedCount,
        shareOfBrowsedPct: pct(savedCount, browsedCount),
      },
    });
    nodeIndices.saved = currentIndex++;
  }

  // Add Applied node if there are any applied jobs
  if (totalApplied > 0) {
    const appliedBeyondBrowse = totalApplied > browsedCount;
    nodes.push({
      name: labels.applied,
      flow: totalApplied,
      jobFlowStage: 'applied',
      tooltipMeta: {
        stage: 'applied',
        jobCount: totalApplied,
        shareOfBrowsedPct:
          !appliedBeyondBrowse && browsedCount > 0 ? pct(totalApplied, browsedCount) : undefined,
        ...(appliedBeyondBrowse ? { appliedBeyondBrowse: true } : {}),
      },
    });
    nodeIndices.applied = currentIndex++;
  }

  // Add further state nodes if they have data
  if (interviewingCount > 0 && labels.interviewing) {
    nodes.push({
      name: labels.interviewing,
      flow: interviewingCount,
      jobFlowStage: 'interviewing',
      tooltipMeta: {
        stage: 'interviewing',
        jobCount: interviewingCount,
        shareOfAppliedPct: pct(interviewingCount, totalApplied),
      },
    });
    nodeIndices.interviewing = currentIndex++;
  }
  if (offerCount > 0 && labels.offer) {
    nodes.push({
      name: labels.offer,
      flow: offerCount,
      jobFlowStage: 'offer',
      tooltipMeta: {
        stage: 'offer',
        jobCount: offerCount,
        shareOfAppliedPct: pct(offerCount, totalApplied),
      },
    });
    nodeIndices.offer = currentIndex++;
  }
  if (rejectedCount > 0 && labels.rejected) {
    nodes.push({
      name: labels.rejected,
      flow: rejectedCount,
      jobFlowStage: 'rejected',
      tooltipMeta: {
        stage: 'rejected',
        jobCount: rejectedCount,
        shareOfAppliedPct: pct(rejectedCount, totalApplied),
      },
    });
    nodeIndices.rejected = currentIndex++;
  }
  if (hiredCount > 0 && labels.hired) {
    nodes.push({
      name: labels.hired,
      flow: hiredCount,
      jobFlowStage: 'hired',
      tooltipMeta: {
        stage: 'hired',
        jobCount: hiredCount,
        shareOfAppliedPct: pct(hiredCount, totalApplied),
      },
    });
    nodeIndices.hired = currentIndex++;
  }

  // Build links array (tooltip meta attached after outflow/inflow totals are known)
  const rawLinks: { source: number; target: number; value: number }[] = [];

  const browsedIndex = nodeIndices.browsed;

  // Browsed -> Viewed (only if viewed-only exists)
  if (viewedOnlyCount > 0 && nodeIndices.viewed !== undefined) {
    rawLinks.push({
      source: browsedIndex,
      target: nodeIndices.viewed,
      value: viewedOnlyCount,
    });
  }

  // Browsed -> Saved (only if saved exists)
  if (savedCount > 0 && nodeIndices.saved !== undefined) {
    rawLinks.push({
      source: browsedIndex,
      target: nodeIndices.saved,
      value: savedCount,
    });
  }

  // Viewed -> Applied (always show link if both nodes exist, even if flow is 0)
  if (nodeIndices.viewed !== undefined && nodeIndices.applied !== undefined) {
    rawLinks.push({
      source: nodeIndices.viewed,
      target: nodeIndices.applied,
      value: appliedFromViewedOnly,
    });
  }

  // Saved -> Applied (always show link if both nodes exist, even if flow is 0)
  if (nodeIndices.saved !== undefined && nodeIndices.applied !== undefined) {
    rawLinks.push({
      source: nodeIndices.saved,
      target: nodeIndices.applied,
      value: appliedFromSaved,
    });
  }

  // Browsed -> Applied (direct from browse, plus off-site / untracked applications shown as inflow)
  const browsedToAppliedValue = appliedFromBrowsed + appliedFromOutsideTrackedFunnel;
  if (browsedToAppliedValue > 0 && nodeIndices.applied !== undefined) {
    rawLinks.push({
      source: browsedIndex,
      target: nodeIndices.applied,
      value: browsedToAppliedValue,
    });
  }

  // Applied -> Further States
  if (nodeIndices.applied !== undefined) {
    if (interviewingCount > 0 && nodeIndices.interviewing !== undefined) {
      rawLinks.push({
        source: nodeIndices.applied,
        target: nodeIndices.interviewing,
        value: interviewingCount,
      });
    }
    if (offerCount > 0 && nodeIndices.offer !== undefined) {
      rawLinks.push({
        source: nodeIndices.applied,
        target: nodeIndices.offer,
        value: offerCount,
      });
    }
    if (rejectedCount > 0 && nodeIndices.rejected !== undefined) {
      rawLinks.push({
        source: nodeIndices.applied,
        target: nodeIndices.rejected,
        value: rejectedCount,
      });
    }
    if (hiredCount > 0 && nodeIndices.hired !== undefined) {
      rawLinks.push({
        source: nodeIndices.applied,
        target: nodeIndices.hired,
        value: hiredCount,
      });
    }
  }

  const outSum = new Map<number, number>();
  const inSum = new Map<number, number>();
  for (const l of rawLinks) {
    outSum.set(l.source, (outSum.get(l.source) ?? 0) + l.value);
    inSum.set(l.target, (inSum.get(l.target) ?? 0) + l.value);
  }

  const links: SankeyLinkData[] = rawLinks.map((l) => {
    const sourceNode = nodes[l.source];
    const targetNode = nodes[l.target];
    const fromStage = sourceNode.jobFlowStage;
    const toStage = targetNode.jobFlowStage;
    if (!fromStage || !toStage) {
      return { ...l };
    }

    const os = outSum.get(l.source) ?? 0;
    const ins = inSum.get(l.target) ?? 0;

    const tooltipMeta: SankeyLinkTooltipMeta = {
      jobCount: l.value,
      fromStage,
      toStage,
      pctOfFromOutflow: pct(l.value, os),
      pctOfToInflow: pct(l.value, ins),
    };

    if (fromStage === 'browsed' && toStage === 'applied') {
      tooltipMeta.fromBrowsedDirect = appliedFromBrowsed;
      tooltipMeta.fromOutsideFunnel = appliedFromOutsideTrackedFunnel;
    }

    return { ...l, tooltipMeta };
  });

  return { nodes, links };
}


